/*eslint max-lines: ["error", 350]*/

import { CognitoUserPool, CognitoUser, AuthenticationDetails } from "amazon-cognito-identity-js";
//Limitless imports
import { log as pilog } from "@limitless/common/utils";
import { handleSecureAjaxError } from "@limitless/portal/utils/SecurePlatformAPIUtils";
export let userPool = null;
let refreshTokenAPICallCount = 0;
let refreshTokenTimeHandler = 0;
const REFRESH_INTERVAL = 14 * 60 * 1000; //The access token refresh after every 14 minutes.

let authenticatedUser = null;
let mfaRequired = null;
export const createUserPool = () => {
  const log = pilog.getLogger("AuthenticationAPI createUserPool");
  const poolData = {
    UserPoolId: process.env.REACT_APP_USER_POOL_ID,
    ClientId: process.env.REACT_APP_CLIENT_ID
  };
  log.debug(poolData);
  userPool = new CognitoUserPool(poolData);
};
let authOptions = null;
export const authenticateUser = (email, password, storageCallback, mfaPhoneChallengeCB, mfaVerifyScreenCallback) => {
  const log = pilog.getLogger("AuthenticationAPI sigininUser");
  return new Promise((resolve, reject) => {
    log.info(`Logging in ${email}, ${password}`);
    const authenticationDetails = new AuthenticationDetails({
      Username: email.toLowerCase().trim(),
      Password: password
    });
    authenticatedUser = new CognitoUser({
      Username: email.toLowerCase().trim(),
      Pool: userPool
    });
    authOptions = {
      onSuccess: result => {
        log.info("Successfully logged into Limitless");
        log.info(result);
        if (mfaRequired) {
          mfaRequired = false;
        }
        refreshTokenTimeHandler = setTimeout(refreshToken, REFRESH_INTERVAL);
        //Specifically introduced for Expert Mobile App.
        if (typeof storageCallback === "function") {
          const data = {
            ...result,
            password
          };
          storageCallback(data, () => {
            resolve(result.idToken);
          });
        } else {
          resolve(result.idToken);
        }
        authenticatedUser.getUserAttributes((err, attributes) => {
          if (err) {
            log.error("Failed to get user attributes", err);
          } else {
            // Find the attribute related to phone number verification
            const phoneNumberVerifiedAttr = attributes.find(attr => attr.Name === "phone_number_verified");
            const phoneNumberExists = attributes.some(item => item.Name === "phone_number");

            // Check if the attribute exists and its value is "true"
            const phoneNumberVerified = (phoneNumberVerifiedAttr === null || phoneNumberVerifiedAttr === void 0 ? void 0 : phoneNumberVerifiedAttr.Value) === "true";

            // If the phone number is not verified, call the MFA verification screen callback
            if (!phoneNumberVerified) {
              mfaVerifyScreenCallback({
                isPhoneNumberVerify: false,
                phoneExistInCognito: true,
                mfaLoginPage: false
              });
            }

            // If the phone number is not present, call the MFA setup screen callback
            if (!phoneNumberExists) {
              mfaVerifyScreenCallback({
                phoneExistInCognito: false,
                isPhoneNumberVerify: false,
                mfaLoginPage: false
              });
            }
          }
        });
      },
      onFailure: err => {
        if (!mfaRequired) {
          handleSecureAjaxError(err, "AuthenticationAPI sigininUser");
          let message = err.message;
          if (message && message.length > 40) {
            message = "User does not exist.";
          }
        }
        reject((err === null || err === void 0 ? void 0 : err.message) || err);
      },
      mfaRequired: (challengeName, challengeParameters) => {
        mfaRequired = true;
        mfaPhoneChallengeCB(challengeName, challengeParameters);
      },
      selectMFAType: (challengeName, challengeParameters) => {
        log.info(`selectMFAType: challengeName ${challengeName}, challengeParameters: ${JSON.stringify(challengeParameters, null, 2)}`);
        authenticatedUser.sendMFASelectionAnswer("SMS_MFA", authOptions);
      }
    };
    authenticatedUser.authenticateUser(authenticationDetails, authOptions);
  });
};

/**
 * Check's to see if the user has valid access tokens within the session.
 *
 * @param {function} callback
 */
export const doesUserHaveValidSession = postTokenRefreshCallback => {
  const log = pilog.getLogger("AuthenticationAPI checkUserAccessTokens");
  log.debug("Attempting to check if the user has valid access tokens");
  authenticatedUser = userPool.getCurrentUser();
  return new Promise((resolve, reject) => {
    if (!authenticatedUser) {
      log.info("The user has no access tokens.");
      reject();
      return;
    }
    log.info("User has signed in before, attempted to resume session");
    authenticatedUser.getSession((err, result) => {
      if (err) {
        log.info("Could not get existing session, user is not authenticated");
        reject();
        return;
      }
      log.info("Successfully received session");
      if (process.env.REACT_APP_TYPE !== "mobile") {
        resetToken(authenticatedUser, result.getRefreshToken());
        resolve(result.idToken);
      } else {
        resetToken(authenticatedUser, result.getRefreshToken(), success => {
          if (success) {
            postTokenRefreshCallback(() => {
              resolve(result.idToken);
            });
          } else {
            reject();
          }
        });
      }
    });
  });
};
export const triggerForgotPassword = email => {
  const log = pilog.getLogger("AuthenticationAPI forgotPassword");
  return new Promise((resolve, reject) => {
    const cognitoUser = new CognitoUser({
      Username: email,
      Pool: userPool
    });
    cognitoUser.forgotPassword({
      onSuccess: data => {
        // successfully initiated reset password request
        log.debug("ForgotPassword response: ", data);
        resolve();
      },
      onFailure: err => {
        handleSecureAjaxError(err, "AuthenticationAPI forgotPassword");
        reject(err.message);
      }
    });
  });
};
export const confirmForgotPassword = (email, verificationCode, newPassword) => {
  const log = pilog.getLogger("AuthenticationAPI confirmPassword");
  return new Promise((resolve, reject) => {
    const cognitoUser = new CognitoUser({
      Username: email,
      Pool: userPool
    });
    cognitoUser.confirmPassword(verificationCode, newPassword, {
      onSuccess() {
        log.info("Password successfully reset");
        resolve();
      },
      onFailure(err) {
        handleSecureAjaxError(err, "AuthenticationAPI confirmPassword");
        reject(err.message);
      }
    });
  });
};
export const signOutUser = username => {
  const log = pilog.getLogger("AuthenticationAPI signOutUser");
  clearTimeout(refreshTokenTimeHandler);
  const cognitoUser = new CognitoUser({
    Username: username,
    Pool: userPool
  });
  try {
    authenticatedUser.globalSignOut({
      onFailure: error => {
        log.info("Failed to globally sign out, will sign out normally", error);
        cognitoUser.signOut();
      },
      onSuccess: () => {
        log.debug("Successfully globally signed out");
      }
    });
  } catch (error) {
    log.info("Error while attempting to globally sign out", error);
    cognitoUser.signOut();
  }
  log.info("Successfully logged out of Limitless");
};
export const changePassword = (oldPassword, newPassword, callback) => {
  const cognitoUser = userPool.getCurrentUser();
  cognitoUser.getSession(() => null);
  cognitoUser.changePassword(oldPassword, newPassword, error => callback(error));
};
export const refreshToken = () => {
  return new Promise((resolve, reject) => {
    const log = pilog.getLogger("AuthenticationAPI refreshToken");
    if (!authenticatedUser) {
      authenticatedUser = userPool.getCurrentUser();
    }
    if (!authenticatedUser) {
      log.info("token: The user has no access tokens.");
      reject();
      return;
    }
    authenticatedUser.getSession((err, result) => {
      if (err) {
        log.info("token: Could not get existing session, user is not authenticated");
        reject();
        return;
      }
      authenticatedUser.refreshSession(result.getRefreshToken(), (err, session) => {
        if (err) {
          reject();
          return;
        }
        resolve(session);
      });
    });
  });
};
const resetToken = (cognitoUser, token, callback) => {
  // Refresh token API calls maximum 5 times before an hour expiry time.
  if (refreshTokenAPICallCount < 5) {
    const log = pilog.getLogger("AuthenticationAPI resetToken");
    refreshTokenAPICallCount++;
    cognitoUser.refreshSession(token, (err, session) => {
      if (err) {
        log.info("token: Could not reset session, user is not authenticated");
        if (typeof callback === "function") {
          callback(false);
        }
        return;
      }
      clearTimeout(refreshTokenTimeHandler);
      refreshTokenTimeHandler = setTimeout(refreshToken, REFRESH_INTERVAL);
      log.info("token: Successfully received new idToken");
      if (typeof callback === "function") {
        callback(true, session);
      }
    });
  }
};
export const sendSMSVerificationCode = () => {
  const log = pilog.getLogger("AuthenticationAPI sendSMSVerificationCode");
  log.debug("Sending SMS Verification Code");
  return new Promise((resolve, reject) => {
    authenticatedUser.getAttributeVerificationCode("phone_number", {
      onSuccess: () => resolve(null),
      inputVerificationCode: data => resolve(data),
      onFailure: err => reject(err)
    });
  });
};
export const updatePhoneAttribute = phoneNumber => {
  const log = pilog.getLogger("AuthenticationAPI updatePhoneAttribute");
  log.debug("phoneNumber", phoneNumber);
  const attributes = [{
    Name: "phone_number",
    Value: phoneNumber
  }];
  return new Promise((resolve, reject) => {
    authenticatedUser.updateAttributes(attributes, (err, data) => {
      if (err) {
        reject(err);
      } else {
        resolve(data);
      }
    });
  });
};
export const verifyPhoneAttribute = code => {
  const log = pilog.getLogger("AuthenticationAPI verifyPhoneAttribute");
  log.debug("code", code);
  return new Promise((resolve, reject) => {
    authenticatedUser.verifyAttribute("phone_number", code, {
      onSuccess: data => resolve(data),
      onFailure: err => reject(err)
    });
  });
};
export const verifyMFALoginCode = (code, mfaLoginSuccessCB, mfaLoginFailureCB) => {
  const log = pilog.getLogger("AuthenticationAPI verifyMFALoginCode");
  log.debug("code", code);
  authenticatedUser.sendMFACode(code, {
    onSuccess: result => {
      mfaLoginSuccessCB();
      authOptions.onSuccess(result);
    },
    onFailure: error => mfaLoginFailureCB(error)
  });
};